import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { CourseRepositoryService } from "./course-repository.service";
import { Observable } from "rxjs";
import { LessonNavigationInfo } from "../models/lesson-navigation-info.model";
import { map } from "rxjs/operators";
import { AppMetaDataItemNames, CourseEntity } from "../../../meta-data/app-meta-data.service";
import { deepCopy, DwActionType, DwMenuActionType, DwMenuService, DwMetaDataRoutingService, DwModalConfig, DwModalService } from "@devwareapps/devware-cap";
import { LessonReferenceInfo } from "../models/lesson-reference-info.model";



@Injectable({ providedIn: 'root' })
export class CourseNavigationService {



    constructor(
        private router: Router, 
        private courseRepositoryService: CourseRepositoryService
        // private dwMetaDataRoutingService: DwMetaDataRoutingService
    ) {

    }

    resumeCourse(courseId: number) {
        this.courseRepositoryService.getCourseProgress(courseId)
            .subscribe(progress => {
                if (progress.furtherestLesson) {
                    this.navigateToLesson({
                        courseId: courseId,
                        moduleId: progress.furtherestLesson.CourseModuleId,
                        lessonId: progress.furtherestLesson.CourseLessonId
                    });

                    return true;
                }
            });
    }

    navigateToMyCourses() {
        const url = `/my-courses`;

        this.router.navigateByUrl(url);
    }

    navigateToCourse(courseId: number) {
        const url = `/my-courses/view/${courseId}`;

        this.router.navigateByUrl(url);
    }

    navigateToModule(courseId: number, moduleId: number) {
        const url = `/my-courses/view/${courseId}/module/${moduleId}`;

        this.router.navigateByUrl(url);
    }

    navigateToLesson(lessonReference: LessonReferenceInfo) {
        const url = `/my-courses/view/${lessonReference?.courseId}/module/${lessonReference?.moduleId}/lesson/${lessonReference?.lessonId}`;

        this.router.navigateByUrl(url);
    }

    getLessonNavigationInfo(lessonReference: LessonReferenceInfo): Observable<LessonNavigationInfo> {
        return this.courseRepositoryService.getCourseHierarchy(lessonReference?.courseId)
            .pipe(map(course => {
                return this.getLessonNavigationInfoFromCourse(course, lessonReference);
            }));
    }

    private getLessonNavigationInfoFromCourse(course: CourseEntity, lessonReference: LessonReferenceInfo): LessonNavigationInfo {

        course = deepCopy(course);
        const lessonNavigationInfo: LessonNavigationInfo = {
            course: course,
            currentLesson: lessonReference,
            isFirstLesson: false,
            isLastLesson: false,
            isFirstModule: false,
            isLastModule: false
        };

        if (course) {
            const orderedModules = [...(course.CourseModule ?? [])]
                .sort((a, b) => (a.ModuleOrder ?? 0) - (b.ModuleOrder ?? 0));

            const currentModule = orderedModules.find(m => m.CourseModuleId == lessonReference.moduleId);
            const orderedLessons = [...(currentModule?.CourseLesson ?? [])]
                .sort((a, b) => (a.LessonOrder ?? 0) - (b.LessonOrder ?? 0));
            const currentLesson = orderedLessons.find(l => l.CourseLessonId == lessonReference.lessonId);

            const nextLesson = orderedLessons.find(m => m.LessonOrder > currentLesson?.LessonOrder && !m.IsPreview);

            if (nextLesson) {
                lessonNavigationInfo.nextLesson = {
                    courseId: course.CourseId,
                    moduleId: currentModule.CourseModuleId,
                    lessonId: nextLesson.CourseLessonId,
                    lessonName: nextLesson.LessonName
                };
            } else {
                const nextModule = orderedModules.find(m => m.ModuleOrder > currentModule?.ModuleOrder);
                const firstLesson = [...(nextModule?.CourseLesson ?? [])]
                    .sort((a, b) => (a.LessonOrder ?? 0) - (b.LessonOrder ?? 0))
                    .find(l => !l.IsPreview);

                if (firstLesson) {
                    lessonNavigationInfo.isLastLesson = true;

                    lessonNavigationInfo.nextLesson = {
                        courseId: course.CourseId,
                        moduleId: nextModule.CourseModuleId,
                        lessonId: firstLesson.CourseLessonId,
                        lessonName: firstLesson.LessonName
                    };
                } else {
                    lessonNavigationInfo.isLastModule = true;
                }
            }

            const previousLesson = [...orderedLessons]
                .reverse()
                .find(m => m.LessonOrder < currentLesson?.LessonOrder && !m.IsPreview);

            if (previousLesson) {
                lessonNavigationInfo.previousLesson = {
                    courseId: course.CourseId,
                    moduleId: currentModule.CourseModuleId,
                    lessonId: previousLesson.CourseLessonId,
                    lessonName: previousLesson.LessonName
                };
            } else {
                const previousModule = [...orderedModules]
                    .reverse()
                    .find(m => m.ModuleOrder < currentModule?.ModuleOrder);
                const previousModuleLesson = [...(previousModule?.CourseLesson ?? [])]
                    .sort((a, b) => (a.LessonOrder ?? 0) - (b.LessonOrder ?? 0))
                    .reverse()
                    .find(l => !l.IsPreview);

                if (previousModuleLesson) {
                    lessonNavigationInfo.isFirstLesson = true;

                    lessonNavigationInfo.previousLesson = {
                        courseId: course.CourseId,
                        moduleId: previousModule.CourseModuleId,
                        lessonId: previousModuleLesson.CourseLessonId,
                        lessonName: previousModuleLesson.LessonName
                    };
                } else {
                    lessonNavigationInfo.isFirstLesson = true;
                    lessonNavigationInfo.isFirstModule = true;
                }
            }
        }


        return lessonNavigationInfo;
    }

    getCurrentCourseParameters(): LessonReferenceInfo {
        return this.getCourseUrlParameters(this.router.url);
    }

    getCourseUrlParameters(url: string): LessonReferenceInfo {
        url = url.replace('//', '/');

        if (url?.indexOf('/my-courses/view/') != 0 && url?.indexOf('/my-courses-new/view/') != 0) {
            return null;
        }

        const result: LessonReferenceInfo = {};

        const parts = url.replace('//', '/').split('/');

        result.courseId = parseInt(parts[3]);

        if (parts.length >= 6) {
            result.moduleId = parseInt(parts[5]);
        }

        if (parts.length >= 8) {
            result.lessonId = parseInt(parts[7]);
        }

        return result;
    }

}
